import React, { FC, ReactNode, useCallback } from 'react';
import {
    DayPicker,
    DayPickerProps,
    DateRange,
    DayPickerRangeProps,
    DayPickerBase,
    DayPickerSingleProps,
} from 'react-day-picker-for-new-calendar';
import dayPickerStyles from 'react-day-picker-for-new-calendar/dist/style.module.css';
import { ru } from 'date-fns/locale';
import cn from 'clsx';

import { Box } from '../Box';
import { Stack } from '../Stack';
import { Container } from './Container';
import { Day } from './Day';
import { CaptionLabel } from './Caption';
import { Months } from './Months';
import { Ranges } from './Ranges';
import { ButtonsContainer } from './ButtonsContainer';
import { TimePicker, TimePickerProps } from './TimePicker';
import styles from './index.module.scss';


type LocaleProps = Partial<Pick<DayPickerProps, 'locale'>>;

const localizationProps: Record<BaseCalendarProps2['locale'], LocaleProps> = {
    ru: {
        locale: ru,
    },
};

export type BaseCalendarProps2<TValue = any> = {
    /** Значение в календаре:
     * - обычный календарь - `Date`;
     * - период - `{ from: Date, to: Date }`;
     * */
    value: TValue;
    /** Обработчик изменения значения, принимающий на вход:
     * - обычный календарь - `Date`;
     * - период - `{ from: Date, to: Date }`;
     * */
    onChange: (value: TValue) => void;
    /** Локализация календаря, в данный момент поддерживается только русская */
    locale?: 'ru';
    /** Кнопки внизу календаря.
     * В случае наличия помещяются в flex-контейнер по дизайну */
    buttons?: ReactNode;
    /** Выставляет автофокус на компонент при появлении */
    initialFocus?: DayPickerBase['initialFocus'];
};

function useBaseProps<TProps extends DayPickerBase>(
    locale: BaseCalendarProps2['locale'] = 'ru',
): TProps {
    return {
        ...localizationProps[locale],
        components: {
            Day,
            Months,
            CaptionLabel,
        },
        classNames: {
            ...dayPickerStyles,
            root: cn(styles.container, dayPickerStyles.root),
            months: cn(styles.months, dayPickerStyles.months),
            row: cn(styles.week, dayPickerStyles.row),
            day: cn(styles.day, dayPickerStyles.day),
            nav: cn(styles.nav, dayPickerStyles.nav),
            cell: cn(styles.cell, dayPickerStyles.cell),
            head_cell: cn(styles.weekDay, dayPickerStyles.head_cell),
            caption: null,
            day_today: cn(styles.today, dayPickerStyles.day_today),
            day_range_start: styles.rangeStart,
            day_range_end: styles.rangeEnd,
            day_range_middle: styles.rangeMiddle,
            day_selected: cn(styles.selected, dayPickerStyles.day_selected),
        },
    } as TProps;
}

export type CalendarProps2 = BaseCalendarProps2<Date> & {
    /** Хранение и установка часа/минуты */
    timePicker?: TimePickerProps;
};

/** Компонент календаря
 *
 * ### Выбор времени
 * Хранение данных времени вынесено за value, так как время должно
 * устанавливаться отдельно по задумке дизайнера
 *
 * @see [Базовая версия](https://www.figma.com/design/nyOI7hySHz5glWUuMIdVpZ/%F0%9F%A7%A9-%5BWeb%5D-Components-Factorin?node-id=4849-10829&m=dev)
 * @see [Выбор времени](https://www.figma.com/design/nyOI7hySHz5glWUuMIdVpZ/%F0%9F%A7%A9-%5BWeb%5D-Components-Factorin?node-id=4849-11451&m=dev)
 */
export const Calendar2: FC<CalendarProps2> = ({
    value,
    onChange,
    locale = 'ru',
    buttons,
    initialFocus,
    timePicker,
}) => {
    const baseProps = useBaseProps<DayPickerSingleProps>(locale);
    return (
        <Container>
            <Stack direction='row'>
                <DayPicker
                    mode='single'
                    selected={value}
                    onDayClick={onChange}
                    initialFocus={initialFocus}
                    {...baseProps}
                />
                {timePicker && (
                    <Box
                        maxHeight='32rem'
                        borderLeft={1}
                        borderColor='grayscale.shade40'
                    >
                        <TimePicker {...timePicker} />
                    </Box>
                )}
            </Stack>
            {buttons && <ButtonsContainer>{buttons}</ButtonsContainer>}
        </Container>
    );
};

export type RangeCalendarProps2 = BaseCalendarProps2<DateRange>;

export const RangeCalendar2: FC<RangeCalendarProps2> = ({
    value,
    onChange,
    locale = 'ru',
    buttons,
    initialFocus,
}) => {
    const baseProps = useBaseProps<DayPickerRangeProps>(locale);
    return (
        <Container>
            <DayPicker
                mode='range'
                selected={value}
                onSelect={useCallback(range => onChange(range), [onChange])}
                numberOfMonths={2}
                modifiers={{
                    isRange: true,
                }}
                modifiersClassNames={{
                    isRange: styles.isRange,
                }}
                initialFocus={initialFocus}
                {...baseProps}
            />
            <Ranges locale={locale} value={value} onChange={onChange} />
            {buttons && <ButtonsContainer>{buttons}</ButtonsContainer>}
        </Container>
    );
};
